import { getApiEndpoint } from '../config/apiEndpoints';
import { fetchWithAuth } from '../utils/tokenRefresh'; // Will be created next

const AUTH_API_URL = getApiEndpoint('AUTH', '/auth');

interface AuthResponse {
  message: string;
  user?: {
    uuid: string;
    username: string;
    email: string;
    is_staff: boolean;
  };
  access_token?: string;
  refresh_token?: string;
}

interface ErrorResponse {
  error: string;
}

const handleResponse = async (response: Response): Promise<AuthResponse> => {
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Something went wrong');
  }
  return data;
};

export const loginUser = async (usernameOrEmail: string, password: string): Promise<AuthResponse> => {
  const response = await fetch(AUTH_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      request_type: 'login',
      username_or_email: usernameOrEmail,
      password: password,
    }),
  });
  const data = await handleResponse(response);
  if (data.access_token && data.refresh_token) {
    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('refresh_token', data.refresh_token);
    if (data.user) {
      localStorage.setItem('user', JSON.stringify(data.user));
    }
  }
  return data;
};

export const signupUser = async (username: string, email: string, password: string): Promise<AuthResponse> => {
  const response = await fetch(AUTH_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      request_type: 'signup',
      username: username,
      email: email,
      password: password,
    }),
  });
  const data = await handleResponse(response);
  if (data.access_token && data.refresh_token) {
    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('refresh_token', data.refresh_token);
    if (data.user) {
      localStorage.setItem('user', JSON.stringify(data.user));
    }
  }
  return data;
};

export const requestPasswordReset = async (email: string): Promise<AuthResponse> => {
  const response = await fetch(AUTH_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      request_type: 'request_password_reset',
      email: email,
    }),
  });
  return handleResponse(response);
};

export const resetPassword = async (token: string, new_password: string): Promise<AuthResponse> => {
  const response = await fetch(AUTH_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      request_type: 'reset_password',
      token: token,
      new_password: new_password,
    }),
  });
  return handleResponse(response);
};

export const logoutUser = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
};

export const refreshAccessToken = async (refreshToken: string): Promise<string> => {
  const response = await fetch(AUTH_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      request_type: 'refresh_token',
      refresh_token: refreshToken,
    }),
  });
  const data = await handleResponse(response);
  if (data.access_token) {
    localStorage.setItem('access_token', data.access_token);
    return data.access_token;
  }
  throw new Error('Failed to refresh access token');
};
